export const FILTROS_PACIENTES_VAZIOS = {
  nome: '',
  codPac: '',
  cpf: '',
  dtNasc: '',
  celular: '',
};

export const BUSCA_PACIENTE_AJUDA =
  'Use um critério: nome (mín. 3 letras), código, CPF completo, celular (mín. 4 dígitos) ou data de nascimento.';

export const BUSCA_PACIENTE_ERRO_VALIDACAO =
  'Informe um critério válido para buscar (veja a dica acima).';

/** Normaliza filtros para comparação draft vs última busca. */
export function normalizeFiltrosForCompare(filtros) {
  if (!filtros) return { ...FILTROS_PACIENTES_VAZIOS };
  return {
    nome: String(filtros.nome ?? '')
      .trim()
      .toUpperCase(),
    codPac: String(filtros.codPac ?? '').trim(),
    cpf: String(filtros.cpf ?? '').replace(/\D/g, ''),
    dtNasc: String(filtros.dtNasc ?? '').trim(),
    celular: String(filtros.celular ?? '').replace(/\D/g, ''),
  };
}

/** Valores enviados ao SWR/API após submit. */
export function normalizeFiltrosForApi(filtros) {
  const nome = String(filtros.nome ?? '').trim();
  const codPac = String(filtros.codPac ?? '').trim();
  const cpf = String(filtros.cpf ?? '').replace(/\D/g, '');
  const dtNasc = String(filtros.dtNasc ?? '').trim();
  const celular = String(filtros.celular ?? '').replace(/\D/g, '');
  return { nome, codPac, cpf, dtNasc, celular };
}

export function filtrosIguais(a, b) {
  const na = normalizeFiltrosForCompare(a);
  const nb = normalizeFiltrosForCompare(b);
  return (
    na.nome === nb.nome &&
    na.codPac === nb.codPac &&
    na.cpf === nb.cpf &&
    na.dtNasc === nb.dtNasc &&
    na.celular === nb.celular
  );
}

export function hasAdvancedFilters(filtros) {
  const n = normalizeFiltrosForCompare(filtros);
  return Boolean(n.codPac || n.cpf || n.celular || n.dtNasc);
}

/**
 * @returns {{ valid: boolean, message?: string, invalidField?: string }}
 */
export function validateBuscaPacientes(filtros) {
  const { nome, codPac, cpf, dtNasc, celular } = normalizeFiltrosForApi(filtros);

  if (codPac && !/^\d+$/.test(codPac)) {
    return {
      valid: false,
      message: 'O código deve conter apenas números.',
      invalidField: 'codPac',
    };
  }

  const temFiltroValido =
    codPac || nome.length >= 3 || cpf.length === 11 || Boolean(dtNasc) || celular.length >= 4;

  if (temFiltroValido) {
    return { valid: true };
  }

  if (nome.length > 0 && nome.length < 3) {
    return {
      valid: false,
      message: 'O nome deve ter pelo menos 3 caracteres.',
      invalidField: 'nome',
    };
  }

  if (cpf.length > 0 && cpf.length < 11) {
    return {
      valid: false,
      message: 'Informe o CPF completo (11 dígitos).',
      invalidField: 'cpf',
    };
  }

  if (celular.length > 0 && celular.length < 4) {
    return {
      valid: false,
      message: 'Informe pelo menos 4 dígitos do celular.',
      invalidField: 'celular',
    };
  }

  return { valid: false, message: BUSCA_PACIENTE_ERRO_VALIDACAO };
}

/**
 * Snapshot persistido em location.state ao sair de /pacientes (ex.: ir para atividades).
 * @typedef {{ nome: string, codPac: string, cpf: string, dtNasc: string, celular: string, showTable: boolean, fromAtividades?: boolean }} PacientesSearchSnapshot
 */

export function normalizePacientesSearch(raw) {
  if (!raw || typeof raw !== 'object') return null;

  return {
    nome: String(raw.nome ?? '').trim(),
    codPac: String(raw.codPac ?? '').trim(),
    cpf: String(raw.cpf ?? '').trim(),
    dtNasc: String(raw.dtNasc ?? '').trim(),
    celular: String(raw.celular ?? '').trim(),
    showTable: Boolean(raw.showTable),
    fromAtividades: Boolean(raw.fromAtividades),
  };
}

export function buildPacientesSearchSnapshot(filtrosBusca, showTablePaciente) {
  return {
    nome: filtrosBusca.nome ?? '',
    codPac: filtrosBusca.codPac ?? '',
    cpf: filtrosBusca.cpf ?? '',
    dtNasc: filtrosBusca.dtNasc ?? '',
    celular: filtrosBusca.celular ?? '',
    showTable: Boolean(showTablePaciente),
  };
}

export function buildPacientesSearchFromAtividadesQuery({ nomePaciente, codpac }) {
  return {
    ...FILTROS_PACIENTES_VAZIOS,
    nome: String(nomePaciente ?? '').trim(),
    codPac: codpac ? String(codpac).trim() : '',
    showTable: true,
    fromAtividades: true,
  };
}

/**
 * @returns {{ filtros: typeof FILTROS_PACIENTES_VAZIOS, showTable: boolean, fromAtividades: boolean, skipAutoOpenModal: boolean }}
 */
export function getInitialSearchFromLocation(location) {
  const fromSnapshot = normalizePacientesSearch(location?.state?.pacientesSearch);
  if (fromSnapshot) {
    const { showTable, fromAtividades, ...filtros } = fromSnapshot;
    return {
      filtros: {
        nome: filtros.nome,
        codPac: filtros.codPac,
        cpf: filtros.cpf,
        dtNasc: filtros.dtNasc,
        celular: filtros.celular,
      },
      showTable: showTable,
      fromAtividades: fromAtividades,
      skipAutoOpenModal: fromAtividades,
    };
  }

  const codPac = location?.state?.codPac ? String(location.state.codPac) : '';
  if (codPac) {
    return {
      filtros: { ...FILTROS_PACIENTES_VAZIOS, codPac },
      showTable: true,
      fromAtividades: false,
      skipAutoOpenModal: false,
    };
  }

  return {
    filtros: { ...FILTROS_PACIENTES_VAZIOS },
    showTable: false,
    fromAtividades: false,
    skipAutoOpenModal: false,
  };
}
